// OpenAPIIntegrationPage.js — Per-country open API integration settings
import React, { useState } from 'react';
import { Globe, Shield, FileText, Wifi, Database, ArrowRightLeft } from 'lucide-react';
import { RESIDENCE_PRIORITY, NATIONALITY_PRIORITY } from '../../../data/countries';

// Source country metadata
const SOURCE_META = {
  'United States':       { code: 'US', currency: 'USD' },
  'Switzerland':         { code: 'CH', currency: 'CHF' },
  'United Arab Emirates':{ code: 'AE', currency: 'AED' },
  'United Kingdom':      { code: 'GB', currency: 'GBP' },
  'Japan':               { code: 'JP', currency: 'JPY' },
};

// Market country metadata
const MARKET_META = {
  Kenya:        { code: 'KE', currency: 'KES' },
  India:        { code: 'IN', currency: 'INR' },
  Brazil:       { code: 'BR', currency: 'BRL' },
  Mexico:       { code: 'MX', currency: 'MXN' },
  Turkey:       { code: 'TR', currency: 'TRY' },
  Bangladesh:   { code: 'BD', currency: 'BDT' },
  'South Africa': { code: 'ZA', currency: 'ZAR' },
  Egypt:        { code: 'EG', currency: 'EGP' },
  Ethiopia:     { code: 'ET', currency: 'ETB' },
};

const buildSourceDefaults = () => {
  const out = {};
  RESIDENCE_PRIORITY.forEach(name => {
    out[name] = {
      regEndpoint: '', regKey: '', complianceEndpoint: '',
      treatyCode: '', withholdingRate: '',
      reportingFrequency: 'quarterly', reportingEndpoint: '', reportingFormat: 'json',
      status: 'disabled',
    };
  });
  return out;
};

const buildMarketDefaults = () => {
  const out = {};
  NATIONALITY_PRIORITY.forEach(name => {
    const isKenya = name === 'Kenya';
    out[name] = {
      cbEndpoint: isKenya ? 'https://api.centralbank.go.ke/v1' : '',
      cbKey: isKenya ? '••••••••••••dk4R' : '',
      cbAuctionApi: isKenya ? 'https://api.centralbank.go.ke/v1/auctions' : '',
      csdEndpoint: isKenya ? 'https://api.dhowcsd.co.ke/v1' : '',
      csdKey: isKenya ? '••••••••••••dk4R' : '',
      fxProvider: isKenya ? 'Reuters' : '',
      fxEndpoint: isKenya ? 'https://api.reuters.com/fx/v2' : '',
      fxKey: isKenya ? '••••••••••••r2Kx' : '',
      regEndpoint: '', regKey: '',
      status: isKenya ? 'active' : 'disabled',
    };
  });
  return out;
};

// Toggle component
const Toggle = ({ enabled, onChange }) => (
  <button
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-black' : 'bg-zinc-300'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const OpenAPIIntegrationPage = () => {
  const [section, setSection] = useState('source');
  const [selectedSource, setSelectedSource] = useState(RESIDENCE_PRIORITY[0]);
  const [selectedMarket, setSelectedMarket] = useState(NATIONALITY_PRIORITY[0]);
  const [sourceConfigs, setSourceConfigs] = useState(buildSourceDefaults);
  const [marketConfigs, setMarketConfigs] = useState(buildMarketDefaults);

  const updateSource = (field, value) => {
    setSourceConfigs(prev => ({
      ...prev,
      [selectedSource]: { ...prev[selectedSource], [field]: value },
    }));
  };

  const updateMarket = (field, value) => {
    setMarketConfigs(prev => ({
      ...prev,
      [selectedMarket]: { ...prev[selectedMarket], [field]: value },
    }));
  };

  const src = sourceConfigs[selectedSource];
  const mkt = marketConfigs[selectedMarket];

  return (
    <div className="space-y-8 font-sans text-black antialiased">
      {/* Header */}
      <div className="border-b border-zinc-200 pb-6">
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-1">Integrations</span>
        <h1 className="font-sans text-3xl font-bold text-black">API Integration</h1>
        <p className="font-sans text-xs text-zinc-600 mt-1">Configure open API connections for investment source jurisdictions and frontier markets</p>
      </div>

      {/* Section tabs */}
      <div className="flex border-b border-zinc-200">
        {[
          { key: 'source', label: 'Investment Source Countries' },
          { key: 'markets', label: 'Investment Markets' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setSection(tab.key)}
            className={`px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] transition-colors rounded-none border-b-2 ${
              section === tab.key
                ? 'text-black font-bold border-[#4D4D4D]'
                : 'text-zinc-500 border-transparent hover:text-black'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── Investment Source Countries ────────────────── */}
      {section === 'source' && (
        <div className="space-y-6">
          {/* Country pills */}
          <div className="flex flex-wrap gap-2">
            {RESIDENCE_PRIORITY.map(name => {
              const m = SOURCE_META[name] || {};
              return (
                <button
                  key={name}
                  onClick={() => setSelectedSource(name)}
                  className={`px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] border transition-all rounded-none ${
                    name === selectedSource
                      ? 'bg-[#4D4D4D] text-white border-[#4D4D4D]'
                      : 'bg-white text-zinc-700 border-zinc-300 hover:border-[#4D4D4D]'
                  }`}
                >
                  {m.code} — {name}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Regulatory API */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <Shield className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Regulatory API</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Endpoint URL</label>
                  <input type="text" value={src.regEndpoint} onChange={e => updateSource('regEndpoint', e.target.value)} placeholder="https://api.regulator.gov/v1" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">API Key</label>
                  <input type="password" value={src.regKey} onChange={e => updateSource('regKey', e.target.value)} placeholder="Enter API key" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Compliance Endpoint</label>
                  <input type="text" value={src.complianceEndpoint} onChange={e => updateSource('complianceEndpoint', e.target.value)} placeholder="https://api.regulator.gov/v1/compliance" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
              </div>
            </div>

            {/* Tax Treaty */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <FileText className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Tax Treaty</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Treaty Code</label>
                  <input type="text" value={src.treatyCode} onChange={e => updateSource('treatyCode', e.target.value)} placeholder="e.g. US-KE-DTA-2014" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Withholding Rate (%)</label>
                  <input type="text" value={src.withholdingRate} onChange={e => updateSource('withholdingRate', e.target.value)} placeholder="e.g. 15" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
              </div>
            </div>

            {/* Reporting */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <FileText className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Reporting</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Frequency</label>
                  <select value={src.reportingFrequency} onChange={e => updateSource('reportingFrequency', e.target.value)} className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white">
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Reporting Endpoint</label>
                  <input type="text" value={src.reportingEndpoint} onChange={e => updateSource('reportingEndpoint', e.target.value)} placeholder="https://api.regulator.gov/v1/reports" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Format</label>
                  <select value={src.reportingFormat} onChange={e => updateSource('reportingFormat', e.target.value)} className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white">
                    <option value="json">JSON</option>
                    <option value="xml">XML</option>
                    <option value="csv">CSV</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <Wifi className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Status</h3>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs font-medium text-zinc-900">Integration Active</p>
                    <p className="text-[10px] font-mono text-zinc-500">Enable API integration for {selectedSource}</p>
                  </div>
                  <Toggle
                    enabled={src.status === 'active'}
                    onChange={() => updateSource('status', src.status === 'active' ? 'disabled' : 'active')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Investment Markets ─────────────────────────── */}
      {section === 'markets' && (
        <div className="space-y-6">
          {/* Country pills */}
          <div className="flex flex-wrap gap-2">
            {NATIONALITY_PRIORITY.map(name => {
              const m = MARKET_META[name] || {};
              return (
                <button
                  key={name}
                  onClick={() => setSelectedMarket(name)}
                  className={`px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] border transition-all rounded-none ${
                    name === selectedMarket
                      ? 'bg-[#4D4D4D] text-white border-[#4D4D4D]'
                      : 'bg-white text-zinc-700 border-zinc-300 hover:border-[#4D4D4D]'
                  }`}
                >
                  {m.code} — {name}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Central Bank API */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <Database className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Central Bank API</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Endpoint URL</label>
                  <input type="text" value={mkt.cbEndpoint} onChange={e => updateMarket('cbEndpoint', e.target.value)} placeholder="https://api.centralbank.gov/v1" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">API Key</label>
                  <input type="password" value={mkt.cbKey} onChange={e => updateMarket('cbKey', e.target.value)} placeholder="Enter API key" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Auction API</label>
                  <input type="text" value={mkt.cbAuctionApi} onChange={e => updateMarket('cbAuctionApi', e.target.value)} placeholder="https://api.centralbank.gov/v1/auctions" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
              </div>
            </div>

            {/* CSD API (cross-ref to Feature 5) */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <Database className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">CSD API</h3>
                <span className="ml-auto text-[10px] font-mono text-zinc-400 uppercase tracking-wider">→ CSD Integration</span>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Endpoint URL</label>
                  <input type="text" value={mkt.csdEndpoint} onChange={e => updateMarket('csdEndpoint', e.target.value)} placeholder="https://api.csd.example/v1" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">API Key</label>
                  <input type="password" value={mkt.csdKey} onChange={e => updateMarket('csdKey', e.target.value)} placeholder="Enter API key" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
              </div>
            </div>

            {/* FX Data Provider */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <ArrowRightLeft className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">FX Data Provider</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Provider Name</label>
                  <input type="text" value={mkt.fxProvider} onChange={e => updateMarket('fxProvider', e.target.value)} placeholder="e.g. Reuters, Bloomberg" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Endpoint URL</label>
                  <input type="text" value={mkt.fxEndpoint} onChange={e => updateMarket('fxEndpoint', e.target.value)} placeholder="https://api.fxprovider.com/v2" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">API Key</label>
                  <input type="password" value={mkt.fxKey} onChange={e => updateMarket('fxKey', e.target.value)} placeholder="Enter API key" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
              </div>
            </div>

            {/* Regulatory Reporting + Status */}
            <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
              <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                <Globe className="w-4 h-4 text-zinc-600" />
                <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Regulatory Reporting</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Endpoint URL</label>
                  <input type="text" value={mkt.regEndpoint} onChange={e => updateMarket('regEndpoint', e.target.value)} placeholder="https://api.regulator.gov/reports" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">API Key</label>
                  <input type="password" value={mkt.regKey} onChange={e => updateMarket('regKey', e.target.value)} placeholder="Enter API key" className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white" />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <div>
                    <p className="font-mono text-xs font-medium text-zinc-900">Integration Active</p>
                    <p className="text-[10px] font-mono text-zinc-500">Enable APIs for {selectedMarket}</p>
                  </div>
                  <Toggle
                    enabled={mkt.status === 'active'}
                    onChange={() => updateMarket('status', mkt.status === 'active' ? 'disabled' : 'active')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpenAPIIntegrationPage;
